import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useMount, usePrevious } from 'react-use';

import {
  generateType,
  types,
  generateSetUiState,
  generateResetUiState,
  UISTATE_REDUCER_KEY,
} from '../components/UIState/utils';

type NameOrFunction<P> = string | ((props: P) => string);
type StateOrFunction<T, P> = T | ((props: P) => T);

interface UseUIStateOptions<T, P> {
  name: NameOrFunction<P>;
  state: StateOrFunction<T, P>;
  persist?: boolean;
}

export function useUIState<T extends object, P>(
  props: P,
  { name, state, persist = false }: UseUIStateOptions<T, P>,
) {
  const dispatch = useDispatch();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const stateName = useMemo(() => (typeof name === 'function' ? name(props) : name), [props]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialState = useMemo(() => (typeof state === 'function' ? state(props) : state), [props]);

  const prevStateName = usePrevious(stateName);

  // selector for this slice of UI state
  const reduxUiState: T =
    useSelector((uiState: any) => uiState[UISTATE_REDUCER_KEY]?.get(stateName));

  // dispatchers (matching your HOC version)
  const add = useCallback((payload: T) => dispatch({
    type: generateType(types.add, stateName),
    payload: {
      name: stateName,
      state: payload,
    },
  }), [dispatch, stateName]);

  // const remove = useCallback(() => dispatch({
  //   type: generateType(types.delete, stateName),
  //   payload: { name: stateName },
  // }), [dispatch, stateName]);

  const reset = useCallback((payload: T) => dispatch({
    type: generateType(types.reset, stateName),
    payload: {
      name: stateName,
      state: payload,
    },
  }), [dispatch, stateName]);

  const set = useCallback((payload: Partial<T>) => dispatch({
    type: generateType(types.set, stateName),
    payload: {
      name: stateName,
      state: payload,
    },
  }), [dispatch, stateName]);

  // Memoized setters (preserves your fnCache logic)
  const resetUiState = useMemo(
    () => generateResetUiState(reset, stateName, () => initialState),
    [reset, stateName, initialState],
  );

  const setUiState = useMemo(
    () => generateSetUiState(set, stateName),
    [set, stateName],
  );

  useMount(() => {
    if (!persist || !reduxUiState) {
      add(initialState);
    }
  });

  useEffect(() => {
    if (prevStateName && prevStateName !== stateName) {
      // Add new state if missing
      if (!reduxUiState) {
        add(initialState);
      }

      // Delete old state if not persistent
      if (!persist) {
        dispatch({
          type: generateType(types.delete, prevStateName),
          payload: { name: prevStateName },
        });
      }
    }
  }, [stateName, prevStateName, add, persist, initialState, reduxUiState, dispatch]);

  return {
    uiState: reduxUiState ?? initialState,
    setUiState,
    setUIState: setUiState, 
    resetUiState,
    resetUIState: resetUiState,
    uiStateName: stateName,
  };
}
